/**
 * This script is added in the head of index.html
 * Initializes things - loading scripts, setting variables/global functions, etc
 */

import { iframeOnlyUrls } from 'shared/constants/HeatmapIframeUrls';

declare var $: JQueryStatic;

// Security warning
// eslint-disable-next-line no-console
console.log('%cWARNING!', 'color: red; background: yellow; font-size: 24px;');
// eslint-disable-next-line no-console
console.log('%cThis section is intended for developers only. Do not enter or paste code that you do not understand. If someone told you to copy and paste something, kindly report this to security@wingify.com.', 'font-size: 18px;');

window.appBaseURI = 'https://' + window.location.host;
window.informVwoExtension = function (data: unknown) {
	try {
		if (window.chrome && window.chrome.runtime && window.chrome.runtime.sendMessage) {
			window.chrome.runtime.sendMessage(window.vwoExtensionId, data);
		}
	} catch (e) {
		// do nothing on error
	}
};

if (window.location.href.includes('routePolicy=vwo')) {
	localStorage.removeItem('isWingifyAppSupported');
}

if (localStorage.getItem('isWingifyAppSupported') && window.location.hostname.includes('vwo.com')) {
	const hostname = window.location.hostname.replace('.vwo.com', '.wingify.com');
	window.location.href = `https://${hostname}${window.location.hash}`;
}

/**
 * Parent HTTP bridge used by editor-embedded iframes.
 * Must match AppIframeCommunication (visual-editor) and the $httpBackend decorator
 * in shared-component-config.ts: VWO_HTTP_REQUEST ↔ VWO_HTTP_RESPONSE.
 */
const VWO_HTTP_REQUEST = 'VWO_HTTP_REQUEST';
const VWO_HTTP_RESPONSE = 'VWO_HTTP_RESPONSE';
const VWO_HTTP_TIMEOUT_MS = 15000;

type VwoHttpBridgeResponse = {
	status: number;
	statusText: string;
	headers: Record<string, string>;
	response: unknown;
};

type VwoHttpRequestOptions = {
	method: string;
	url: string;
	headers?: Record<string, string>;
	data?: unknown;
	withCredentials?: boolean;
	responseType?: string;
	timeoutMs?: number;
};

let nextVwoHttpCorrelationId = 1;

function requestViaParentHttp(options: VwoHttpRequestOptions): Promise<VwoHttpBridgeResponse> {
	return new Promise((resolve, reject) => {
		if (!window.parent || window.parent === window) {
			reject(new Error('Editor parent window is not available for HTTP bridge'));
			return;
		}

		const correlationId = nextVwoHttpCorrelationId++;
		const timeoutMs = options.timeoutMs ?? VWO_HTTP_TIMEOUT_MS;
		let settled = false;
		let timeoutId: number | undefined;

		const cleanup = () => {
			if (timeoutId !== undefined) {
				window.clearTimeout(timeoutId);
			}
			window.removeEventListener('message', onMessage);
		};

		const settle = (fn: () => void) => {
			if (settled) {
				return;
			}
			settled = true;
			cleanup();
			fn();
		};

		const onMessage = (event: MessageEvent) => {
			const data = event.data as {
				type?: string;
				correlationId?: number;
				status?: number;
				statusText?: string;
				headers?: Record<string, string>;
				response?: unknown;
			} | null;

			if (!data || data.type !== VWO_HTTP_RESPONSE || data.correlationId !== correlationId) {
				return;
			}

			settle(() => {
				resolve({
					status: typeof data.status === 'number' ? data.status : 0,
					statusText: data.statusText || '',
					headers: data.headers || {},
					response: data.response
				});
			});
		};

		window.addEventListener('message', onMessage);

		timeoutId = window.setTimeout(() => {
			settle(() => {
				reject(new Error(`Timed out waiting for ${VWO_HTTP_RESPONSE} (correlationId=${correlationId})`));
			});
		}, timeoutMs);

		try {
			window.parent.postMessage({
				type: VWO_HTTP_REQUEST,
				correlationId,
				method: options.method,
				url: options.url,
				data: options.data,
				headers: options.headers || {},
				withCredentials: !!options.withCredentials,
				responseType: options.responseType || 'json'
			}, '*');
		} catch (error) {
			settle(() => {
				reject(error instanceof Error ? error : new Error('Failed to send VWO_HTTP_REQUEST to editor parent'));
			});
		}
	});
}

(function () {
	class Deferred {
		promise: Promise<any>;
		resolve!: (value: any) => void;
		reject!: (reason?: any) => void;
		constructor() {
			this.promise = new Promise((resolve, reject) => {
				this.resolve = resolve;
				this.reject = reject;
			});
		}
	}
	let endpoint = '/login';
	const accountsEndpoint = '/user/accounts';
	// const region = localStorage.getItem('lastDataRegionCacheKey')
	// if (region) {
	// 	endpoint = `/${region.toLowerCase()}${endpoint}`;
	// }
	const currentTheme = localStorage.getItem('vwo-theme');
	const isDark = currentTheme === 'dark' || (currentTheme === 'auto' && window.matchMedia('(prefers-color-scheme: dark)').matches);
	// Apply light theme for iframe-only URLs to prevent dark flash when parent app uses dark theme
	if (iframeOnlyUrls.some((url) => window.location.href.includes(url))) {
		document.documentElement.setAttribute('data-theme', 'light');
	} else if (isDark) {
		document.documentElement.setAttribute('data-theme', 'dark');
	}
	const deferred = new Deferred();
	window.firstLoginCallPromise = deferred.promise;
	window.firstLoginCallPromise.catch(() => {
		delete window.firstLoginCallPromise;
	})
	const urlType = new URLSearchParams(window.location.search).get('type');
	if (urlType === 'editor-copilot' || urlType === 'editor-copilot-v2' || urlType === 'editor-metric' || urlType === 'editor-v2-metric') {
		// Editor embeds app-v3 in a cross-origin iframe. Cookie-backed /login + /user/accounts
		// must ride the parent HTTP bridge (VWO_HTTP_REQUEST) that AppIframeCommunication handles.
		let searchParams = location.hash.split('?')[1] || '';

		let loginUrl = window.appBaseURI + endpoint;
		let accountsUrl = window.appBaseURI + accountsEndpoint;

		const playerToken = new URLSearchParams(searchParams).get('playerToken');
		const authHeaders: Record<string, string> = playerToken ? { 'x-vwo-player-token': playerToken } : {};

		Promise.all([
			requestViaParentHttp({ method: 'GET', url: loginUrl, headers: authHeaders, withCredentials: true }),
			requestViaParentHttp({ method: 'GET', url: accountsUrl, headers: authHeaders, withCredentials: true })
		])
			.then(([loginResponse, accountsResponse]) => {
				const data = (typeof loginResponse.response === 'string'
					? JSON.parse(loginResponse.response)
					: loginResponse.response) as Record<string, unknown>;

				const accountsPayload = typeof accountsResponse.response === 'string'
					? JSON.parse(accountsResponse.response)
					: accountsResponse.response;

				const accountsList = (accountsPayload && typeof accountsPayload === 'object' && 'accounts' in (accountsPayload as Record<string, unknown>))
					? (accountsPayload as Record<string, unknown>).accounts
					: accountsPayload;

				window.amILoggedIn = true;
				data.accounts = accountsList;
				deferred.resolve(data);

				// HACK: same as normal login path for local/dev when login.js loads via ajax.
				// Use a successful response-shaped object; empty XMLHttpRequest would mark user logged out.
				const request = {
					status: 200,
					responseText: JSON.stringify(data)
				} as XMLHttpRequest;

				let attempts = 0;
				const loginModuleCheckInterval = setInterval(function () {
					attempts++;
					if (window.login) {
						clearInterval(loginModuleCheckInterval);
						window.login.init(request);
					} else if (attempts >= 40) {
						clearInterval(loginModuleCheckInterval);
					}
				}, 50);
			})
			.catch((error) => {
				deferred.reject(error);
			});
		return;
	}

	var request = new XMLHttpRequest(),
		loginUrl = window.appBaseURI + endpoint,
		accountsUrl = window.appBaseURI + accountsEndpoint,
		searchParams = location.hash.split('?')[1] || '';
		//ignore searchParams for insightsDashboard
		if (searchParams.includes('insightsDashboard')){
			searchParams = ''
		}

	//Forward the search params in the login call.
	//Except for forget-password page. In this case we don't need to send the hash as it is sensitive information.
	if (searchParams && location.hash.indexOf('#/forgot-password') !== 0) {
		loginUrl += '?' + searchParams;
		accountsUrl += '?' + searchParams;
	}
	request.withCredentials = true;
	request.open('GET', loginUrl, true);


	// Parse search params to get token
	const playerToken = new URLSearchParams(searchParams).get('playerToken');
	if (playerToken) {
		request.setRequestHeader('x-vwo-player-token', playerToken);
	}

	request.onerror = () => deferred.reject();
	request.onload = function() {
		if (request.status === 401) {
			if (location.protocol === 'http:') {
				location.protocol = 'https:';
				return;
			}
		}

		if (request.status === 200) {
			window.amILoggedIn = true;
			let data = JSON.parse(request.responseText);
			let accountsListPromise = fetch(accountsUrl, {
				headers: playerToken ? {
					'x-vwo-player-token': playerToken
				} : {}
			}).then(response => response.json());
			accountsListPromise.then((accountsList) => {
				data.accounts = accountsList;
				deferred.resolve(data);
			});
		} else {
			deferred.reject('Login call status: ' + request.status + ' ' + request.statusText);
		}
		// HACK: require only for local dev where login.js is fetched
		// through ajax
		var loginModuleCheckInterval = setInterval(function () {
			if (window.login) {
				clearInterval(loginModuleCheckInterval);
				window.login.init(request);
			}
		}, 50);
	};

	//if (location.protocol === 'http:') {
		request.send();
	//}
})();


window.VB9272 = {
	isSmoothOnboarding: undefined,
	setVariation(isSmoothOnboarding = false) {
		this.isSmoothOnboarding = isSmoothOnboarding;
	}
};

window.showWebEngage = function () {
	if (!window.$) {
		return;
	}
	window.dataLayer = window.dataLayer || [];
	window.dataLayer.push({
		event: 'showWebengage'
	});
	$('#webengage-hider').remove();
};

window.hideWebEngage = function () {
	if (!window.$) {
		return;
	}
	if ($('#webengage-hider').length){
		return;
	}
	$('head').append('<style id="webengage-hider" type="text/css">#webklipper-publisher-widget-container-survey-content {display: none !important}</style>');
};


(function () {
	var head = document.getElementsByTagName('head')[0]!;
	var loadCss = window.loadCss = function loadCss(href: string) {
		var l = document.createElement('link');
		l.rel = 'stylesheet';
		// HACK: Trick to make styleheet not block rendering
		l.media = 'dummy';
		l.href = window.appBaseURI + '/' + href;
		head.appendChild(l);
		l.onload = function () {
			//HACK for FF41
			setTimeout(function (){
				l.media = 'all';
			}, 0);
		};
		return window;
	};

	window.loadCsses = function(cssList: string | string[]) {
		if (cssList) {
			if (Object.prototype.toString.call(cssList) === '[object Array]') {
				for (var i = 0; i < cssList.length; i++) {
					loadCss(cssList[i]!);
				}
			} else if (typeof cssList === 'string') {
				loadCss(cssList);
			}
		}

		return window;
	};
})();

window.isABTMergedTheme = function() {
	return localStorage.getItem('isABTMergedTheme') === 'true';
}

window.setABTMergedTheme = function(isABTMergedTheme?: boolean) {
	localStorage.setItem('isABTMergedTheme', Boolean(isABTMergedTheme).toString());
	updateAbtThemeAttribute(isABTMergedTheme);
}

function updateAbtThemeAttribute(isAbtThemeEnabled?: boolean) {
	if (isAbtThemeEnabled) {
		document.documentElement.setAttribute('data-abt-theme', 'true');
	} else {
		document.documentElement.removeAttribute('data-abt-theme');
	}
}

updateAbtThemeAttribute(window.isABTMergedTheme());

